import { API } from '@/shared/api/modules';
import {
  DashboardInsightAuthError,
  type DashboardInsightTab,
  type InsightBlock,
} from '@/shared/api/modules/facilities';
import { queryOptions } from '@tanstack/react-query';

type GetDashboardInsightArgs = {
  activeDashboard: DashboardInsightTab;
  from: string;
  to: string;
  facilitiesIds?: number[];
  enabled: boolean;
};

/**
 * The insight as the panel sees it — possibly still being written.
 *
 * `complete` is false while cards are streaming in. Until then `summary` may be
 * missing and `blocks` is in the order the model wrote them; the final answer
 * replaces both, sorted.
 */
export type DashboardInsightView = {
  summary: string | null;
  blocks: InsightBlock[];
  generatedAt?: string;
  cached?: boolean;
  complete: boolean;
};

/**
 * The query key carries the regeneration triggers from the brief: a facility-
 * or date-filter change changes the key. No manual refetch wiring is needed.
 *
 * The insight streams. Each card is written into this query's cache as soon as
 * the server sends it, so the panel renders the first card while the model is
 * still writing the rest — and anything else watching the same key (a prefetch
 * started before the panel opened) shares the same single request.
 */
export const getDashboardInsightQueryOptions = ({
  activeDashboard,
  from,
  to,
  facilitiesIds,
  enabled,
}: GetDashboardInsightArgs) => {
  // Sorted so that picking the same facilities in a different order is one key,
  // matching how the server hashes the scope.
  const scopedIds = facilitiesIds?.length
    ? [...facilitiesIds].sort((a, b) => a - b)
    : undefined;

  const params = {
    activeDashboard,
    from,
    to,
    ...(scopedIds ? { facilitiesIds: scopedIds } : {}),
  };

  return queryOptions({
    queryKey: [
      'facilities-dashboard-insight',
      activeDashboard,
      from,
      to,
      scopedIds,
    ],
    enabled: enabled && Boolean(from && to),
    // The server trusts a stored insight for 5 minutes, then re-checks it against
    // the dashboard numbers and regenerates if one moved (Anna, ST #182 comment
    // #440). The browser has to ask at least that often, or it keeps showing the
    // old text however fresh the server's answer is — the dashboard cards refresh
    // on the same 5-minute cadence.
    staleTime: 1000 * 60 * 5,
    // Generation is expensive — one retry, not the default three.
    retry: 1,
    queryFn: async ({
      client,
      queryKey,
      signal,
    }): Promise<DashboardInsightView> => {
      let partial: DashboardInsightView | null = null;

      try {
        const insight = await API.facilities.streamDashboardInsight(params, {
          signal,
          onEvent: (event) => {
            if (event.type !== 'summary' && event.type !== 'block') return;

            // The first streamed event replaces whatever was cached before
            // (a stale answer being refreshed); later ones build on it.
            partial ??= { summary: null, blocks: [], complete: false };
            partial =
              event.type === 'summary'
                ? { ...partial, summary: event.summary }
                : { ...partial, blocks: [...partial.blocks, event.block] };

            // `updatedAt: 0` marks what has streamed so far as already stale. If
            // the request is cut off — the user changes a filter mid-stream, which
            // aborts it — this partial answer stays in the cache, and with a
            // 5-minute staleTime it would be treated as a finished one: going
            // back to that scope showed half an insight, or an error, and never
            // asked again. Stale, it is refetched the moment the scope is shown.
            client.setQueryData<DashboardInsightView>(queryKey, partial, {
              updatedAt: 0,
            });
          },
        });

        return { ...insight, complete: true };
      } catch (error) {
        if (!(error instanceof DashboardInsightAuthError)) throw error;

        // Expired session: the axios path refreshes the token and retries.
        // Slower — no streaming — but only for this one request.
        const insight = await API.facilities.postDashboardInsight(params, {
          signal,
        });
        return { ...insight, complete: true };
      }
    },
  });
};
